import {
  Down,
  Star,
  Up,
  Phone,
  Link as LinkIcon,
  Whatsapp,
  OnelivV2,
  StarFill,
  Users,
} from "@repo/icons";
import { CpsAvatar, CpsButtonText } from "corpus";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import {
  CommunityListFollowersQueryDocument,
  CommunityListSearchQueryDocument,
} from "@repo/graphql-types/graphql";
import { graphql } from "@repo/graphql-types/gql";
import { Browser } from "@capacitor/browser";
import { recommendationMemberAtom } from "@/lib/atoms/recommendation-member-atom";
import {
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { CommunityCardUserLocations } from "@/components/community-card-user-locations";

interface CommunityCardItemListProps {
  codUsuario: number;
  memberName: string;
  specialty: string;
  locations: string[];
  website: string;
  phone: string;
  oneliv: string;
  whatsapp: string;
  photoUrl?: string;
  isFavorite?: boolean;
}

const HandleFavoriteMemberMutation = graphql(/* GraphQL */ `
  mutation HandleFavoriteMemberMutation($codUsuarioFollowing: Int!) {
    insert_nexus_follow_one(object: { codUsuarioFollowing: $codUsuarioFollowing }) {
      id
    }
  }
`);

const HandleNotFavoriteMemberMutation = graphql(/* GraphQL */ `
  mutation HandleNotFavoriteMemberMutation($codUsuarioFollowing: Int!) {
    delete_nexus_follow(where: { codUsuarioFollowing: { _eq: $codUsuarioFollowing } }) {
      affected_rows
    }
  }
`);

export const CommunityCardItemList = ({
  codUsuario,
  memberName,
  specialty,
  locations,
  website,
  phone,
  oneliv,
  whatsapp,
  photoUrl,
  isFavorite,
}: CommunityCardItemListProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFavoriteClicked, setIsFavoriteClicked] = useState(isFavorite);
  const [showParticles, setShowParticles] = useState(false);
  const setMemberAtomValues = useSetAtom(recommendationMemberAtom);

  const invalidate = useInvalidateQuery(CommunityListSearchQueryDocument);
  const invalidateFollowing = useInvalidateQuery(CommunityListFollowersQueryDocument);

  const { mutateAsync: mutateFollowAsync } = useGraphQLMutationWithErrorHandler(
    HandleFavoriteMemberMutation,
  );

  const { mutateAsync: mutateNotFollowAsync } = useGraphQLMutationWithErrorHandler(
    HandleNotFavoriteMemberMutation,
  );

  const handleFavoriteClick = async (codUsuarioFollowing: number): Promise<void> => {
    const mutation = isFavoriteClicked ? mutateNotFollowAsync : mutateFollowAsync;

    await mutation(
      {
        codUsuarioFollowing,
      },
      {
        onSuccess: () => {
          setIsFavoriteClicked(!isFavoriteClicked);
          setShowParticles(!isFavoriteClicked);
          invalidate();
          invalidateFollowing();
        },
      },
    );
  };

  const particles = Array.from({ length: 8 }, (_, x) => x + 1);

  const onSiteClick = async (site: string | undefined): Promise<void> => {
    if (site) {
      await Browser.open({
        url: site,
      });
    }
  };

  return (
    <div className="flex flex-col border border-solid border-neutral-200 rounded-sm">
      <div className="flex flex-row gap-4 py-4 px-4">
        <CpsAvatar initials={memberName} color="primaryLight" src={photoUrl} />
        <div className="flex flex-col w-full">
          <div className="flex flex-row gap-2 items-center justify-between w-full">
            <h2 className="text-lg font-medium text-neutral-600">{memberName}</h2>

            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              {isOpen ? (
                <Up size={16} className="fill-neutral-400" />
              ) : (
                <Down size={16} className="fill-neutral-400" />
              )}
            </button>
          </div>
          <p className="text-neutral-500">{specialty}</p>

          <div className="flex flex-row gap-4 items-center justify-between w-full">
            <CommunityCardUserLocations locations={locations} />
            <div className="flex items-center gap-2">
              <div className="relative">
                <motion.button
                  onClick={() => {
                    void handleFavoriteClick(codUsuario);
                  }}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors relative"
                  whileTap={{ scale: 0.9 }}
                >
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      rotate: isFavoriteClicked ? [0, 15, 0] : [0, -15, 0],
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {isFavoriteClicked ? (
                      <StarFill className="w-6 h-6 fill-primary-400" />
                    ) : (
                      <Star className="w-6 h-6 fill-primary-400 " />
                    )}
                  </motion.div>
                </motion.button>
                <AnimatePresence>
                  {showParticles ? (
                    <>
                      {particles.map((particle, index) => (
                        <motion.span
                          key={`particle-${particle.valueOf()}`}
                          initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
                          animate={{
                            scale: 1,
                            x: (index % 2 ? 1 : -1) * (10 + Math.random() * 10),
                            y: (index % 4 < 2 ? -1 : 1) * (10 + Math.random() * 10),
                            opacity: 0,
                          }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.6, ease: "easeOut" }}
                          className="absolute w-1 h-1 bg-amber-400 rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                        />
                      ))}
                      {!isFavorite ? (
                        <motion.span
                          initial={{ scale: 0.8, opacity: 1 }}
                          animate={{ scale: 2, opacity: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.6 }}
                          className="absolute inset-0 border-2 border-amber-400 rounded-full"
                        />
                      ) : null}
                    </>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isOpen ? (
        <div className="px-4 pb-4 pt-2 space-y-3">
          {oneliv ? (
            <button
              type="button"
              onClick={() => {
                void onSiteClick(oneliv);
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <OnelivV2 size={24} />
              <span className="text-sm underline">Perfil no Oneliv</span>
            </button>
          ) : null}

          {website ? (
            <button
              type="button"
              onClick={() => {
                void onSiteClick(website);
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 align-middle"
            >
              <LinkIcon className="fill-primary-500" size={24} />
              <span className="text-sm underline">Site do profissional</span>
            </button>
          ) : null}

          {whatsapp ? (
            <a
              href={`https://wa.me/+55${whatsapp.replace(/\D/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <span className="text-xl">
                <Whatsapp className="fill-primary-500" size={24} />
              </span>
              <span className="text-sm underline">{whatsapp}</span>
            </a>
          ) : null}

          {phone ? (
            <a
              href={`tel:${phone}`}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <Phone className="fill-primary-500" size={24} />
              <span className="text-sm underline">{phone}</span>
            </a>
          ) : null}

          <div className="mt-3 mx-[-6.5px]">
            <Link
              to="/recommendation/"
              search={{ userId: codUsuario }}
              onClick={() =>
                setMemberAtomValues({
                  id: codUsuario,
                  name: memberName,
                  specialty,
                })
              }
            >
              <CpsButtonText Icon={Users} color="secondary">
                Indicar paciente
              </CpsButtonText>
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
};
